/**
 * Element and PCB node label action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import type {IElementData, NetLabelMap} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";

import type {DocumentStore} from "../document";

/**
 * Hook to get the updateElementsLabel action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to update element labels
 */
export function useUpdateElementsLabel(): (elements: IElementData[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elements: IElementData[]) => {
            documentStore.getState().updateElementsLabel(elements);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setNodeLabel action for PCB nodes
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to set a PCB node's label
 */
export function useSetNodeLabel(): (nodeUid: string, label: string, canUndo?: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (nodeUid: string, label: string, canUndo = true) => {
            documentStore.getState().setNodeLabel(nodeUid, label, canUndo);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setNodeLabelMulti action for multiple PCB nodes
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to set labels on multiple PCB nodes
 */
export function useSetNodeLabelMulti(): (nodeUidLabelMap: {[nodeUid: string]: string}) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (nodeUidLabelMap: {[nodeUid: string]: string}) => {
            documentStore.getState().setNodeLabelMulti(nodeUidLabelMap);
        },
        [documentStore],
    );
}

/**
 * Hook to get the updateNetsLabel action for net labels
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to update net labels
 */
export function useUpdateNetsLabel(): (netLabelMap: NetLabelMap, canUndo?: boolean) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (netLabelMap: NetLabelMap, canUndo = true) => {
            documentStore.getState().updateNetsLabel(netLabelMap, canUndo);
        },
        [documentStore],
    );
}

/**
 * Factory function to create element label actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch element label actions.
 *
 * @param documentStore - The document dispatcher from FluxServices
 * @returns An object with element label action methods
 */
export function createElementLabelActions(documentStore: DocumentStore) {
    return {
        updateElementsLabel: (elements: IElementData[]) => {
            documentStore.getState().updateElementsLabel(elements);
        },
        setNodeLabel: (nodeUid: string, label: string, canUndo = true) => {
            documentStore.getState().setNodeLabel(nodeUid, label, canUndo);
        },
        setNodeLabelMulti: (nodeUidLabelMap: {[nodeUid: string]: string}) => {
            documentStore.getState().setNodeLabelMulti(nodeUidLabelMap);
        },
        updateNetsLabel: (netLabelMap: NetLabelMap, canUndo = true) => {
            documentStore.getState().updateNetsLabel(netLabelMap, canUndo);
        },
    };
}
